import React, { Suspense } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CacheBuster } from 'CacheBuster';
import GlobalLoader from 'components/elements/GlobalLoader';
import { ModalRouter } from 'components/modals/ModalRouter';
import PinnedProjects from 'components/widgets/Dashboard/PinnedProjects';
import SharedProjects from 'components/widgets/Dashboard/SharedProjects';
import SomethingWentWrong from 'components/widgets/Error/SomethingWentWrong';
import AccountSettingsPage from 'pages/AccountSettings';
import { Billing } from 'pages/AccountSettings/Billing';
import { Integrations } from 'pages/AccountSettings/Integrations';
import { Notifications } from 'pages/AccountSettings/Notifications';
import { Profile } from 'pages/AccountSettings/Profile';
import { Users } from 'pages/AccountSettings/Users';
import EmailVerificationPage from 'pages/Authentication/EmailVerification';
import EmailVerifiedConfirmation from 'pages/Authentication/EmailVerifiedConfirmation';
import Login from 'pages/Authentication/Login';
import PasswordResetPage from 'pages/Authentication/PasswordReset';
import SignUp from 'pages/Authentication/SignUp';
import { CompanyDetails } from 'pages/CompanyDetails';
import CompanyGroups from 'pages/CompanyGroups';
import { CompanySearch } from 'pages/CompanySearch';
import { DashboardPage } from 'pages/Dashboard';
import ProjectGroupEdit from 'pages/Dashboard/ProjectGroupEdit';
import DocumentSearch from 'pages/DocumentSearch';
import FeatureFlags from 'pages/FeatureFlags';
import PrintProjectDetails from 'pages/PrintProjectDetails';
import ProjectDetailsPage from 'pages/ProjectDetails';
import ProjectGroups from 'pages/ProjectGroups';
import { ProjectSearch } from 'pages/ProjectInsights';
import ScoutAgent from 'pages/ScoutAgent';
import ForceLogoutRoute from 'routes/ForceLogoutRoute';
import 'scss/index.scss';
import PrivateRoute from 'routes/PrivateRoute';
import PublicRoute from 'routes/PublicRoute';
import SignupRoute from 'routes/SignupRoute';
import UnverifiedPrivateRoute from 'routes/UnverifiedPrivateRoute';
import AxiosInterceptor from 'services/API/axios/AxiosInterceptor';
import theme from 'theme';

const renderFallback = () => <SomethingWentWrong />;

export const App = () => {
  return (
    <ChakraProvider theme={theme}>
      <ErrorBoundary fallbackRender={renderFallback} key="global">
        <Suspense fallback={<GlobalLoader />}>
          <AxiosInterceptor>
            <>
              <CacheBuster />
              <Routes>
                <Route element={<ForceLogoutRoute />}>
                  <Route
                    path="/reset-password/:resetToken"
                    element={<PasswordResetPage />}
                  />
                </Route>
                <Route element={<UnverifiedPrivateRoute />}>
                  <Route path="/email-verification" element={<EmailVerificationPage />} />
                  <Route path="/verified-email" element={<EmailVerifiedConfirmation />} />
                </Route>
                <Route element={<PublicRoute />}>
                  <Route path="/login" element={<Login />} />
                </Route>
                <Route element={<SignupRoute />}>
                  <Route path="/sign-up" element={<SignUp />} />
                </Route>
                <Route element={<PrivateRoute />}>
                  <Route path="feature-flags" element={<FeatureFlags />} />
                  <Route path="/account-settings" element={<AccountSettingsPage />}>
                    <Route index element={<Profile />} />{' '}
                    {/* This line makes <Profile /> the default */}
                    <Route path="my-profile" element={<Profile />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="users" element={<Users />} />
                    <Route path="billing" element={<Billing />} />
                    <Route path="integrations" element={<Integrations />} />
                  </Route>
                  <Route path="/companies/:id/*" element={<CompanyDetails />} />
                  <Route path="saved-search/:id/edit" element={<ProjectGroupEdit />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ErrorBoundary fallbackRender={renderFallback} key="dashboard">
                        <DashboardPage />
                      </ErrorBoundary>
                    }
                  >
                    <Route path="shared" element={<SharedProjects />} />
                    <Route path="pinned" element={<PinnedProjects />} />
                    <Route path="projects" element={<ProjectGroups />} />
                    <Route path="companies" element={<CompanyGroups />} />
                  </Route>
                  <Route path="/*" element={<Navigate to="/dashboard" />} />
                  <Route
                    path="/companies"
                    element={
                      <ErrorBoundary fallbackRender={renderFallback} key="company-search">
                        <CompanySearch />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ErrorBoundary fallbackRender={renderFallback} key="project-map">
                        <ProjectSearch />
                      </ErrorBoundary>
                    }
                  />
                  <Route path="/projects/:id/print" element={<PrintProjectDetails />} />
                  <Route path="/projects/:id/*" element={<ProjectDetailsPage />} />
                  <Route path="/documents" element={<DocumentSearch />} />
                  <Route path="/scout-agent" element={<ScoutAgent />} />
                </Route>
              </Routes>
              <ModalRouter />
            </>
          </AxiosInterceptor>
        </Suspense>
      </ErrorBoundary>
    </ChakraProvider>
  );
};
